import React, { useContext, useEffect, useMemo, useState } from 'react'
import {
    HiOutlineMail,
    HiOutlineExclamationCircle,
    HiOutlineCheckCircle,
    HiOutlineBan,
    HiCheck,
    HiX,
} from 'react-icons/hi'
import Modal from '../../shared/Modal'
import api from '../../../utils/api'
import { CaseDetailContext } from '../CaseDetailContext'
import {
    round2,
    fmtMoney,
    fmtFee,
    initialsOf,
    roleWord,
    splitWhole,
    byClientIdAsc,
    collectExistingParties,
    collectRemovableParties,
    collectHourlyRows,
    buildHourlyMaps,
    hasGeneratedInvoice,
    validateAddParty,
    parseEmailFieldError,
    removalBlockedReason,
} from './partyUtils'

/**
 * "Modify parties" — the case-manager portal's port of the mediator portal's
 * AddNewPartyModal. One multi-mode dialog, not two.
 *
 * MODES
 *   add    — 3-step wizard (cases with no invoices skip the split step -> 2):
 *              1. Party type (side toggle) + name (req) + email (req) + address
 *              2. Invoice split — per-side rows with editable amounts,
 *                 Even / 50-50 / Custom; informational expected-vs-current note.
 *              3. Notifications — invoice re-issue and onboarding-link toggles.
 *   remove — pick a party, preview how the survivors re-split, then choose what
 *            to email. Primary and additional parties use different endpoints.
 *   replace — entered from remove when the selected party is the last of its
 *            side: add the new party first, then remove the old one.
 *
 * LOCKED — once a real payment exists on the case, parties cannot be modified.
 *
 * The case manager acts ON BEHALF OF the mediator: cm-be proxies to the same
 * scale-be orchestrators, so the invoices and emails are identical to the
 * mediator doing this themselves. Only the authorization differs.
 *
 * DELIBERATE DIVERGENCES from the mediator portal, and why:
 *   - No react-multi-email / yup. Both would be new dependencies (and a
 *     package-lock churn) for one form, so the email chips and the validation
 *     are inline; the validation messages are copied verbatim.
 *   - Hourly amounts come from `caseData.*.hourlyPayment`, which cm-be already
 *     attaches per party, instead of a second HTTP fetch.
 */

/** Step bar — numbered circles + connecting lines. */
const StepBar = ({ step, steps }) => (
    <div className="flex items-center px-6 py-4 border-b border-gray-100">
        {steps.map((label, i) => {
            const n = i + 1
            const done = n < step
            const active = n === step
            return (
                <React.Fragment key={label}>
                    <div className="flex items-center gap-2">
                        <span
                            className={`flex h-5 w-5 items-center justify-center rounded-full text-[11px] font-semibold ${
                                active
                                    ? 'bg-teal-800 text-white'
                                    : done
                                      ? 'bg-teal-100 text-teal-700'
                                      : 'bg-gray-100 text-gray-400 border border-gray-200'
                            }`}
                        >
                            {done ? <HiCheck size={12} /> : n}
                        </span>
                        <span className={`text-[11px] ${active ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
                            {label}
                        </span>
                    </div>
                    {i < steps.length - 1 && <span className="flex-1 h-px bg-gray-100 mx-2" />}
                </React.Fragment>
            )
        })}
    </div>
)

/**
 * One editable party row (avatar + name/role + right-aligned amount).
 * The input edits the MEDIATION (base) fee; the admin fee is shown separately
 * underneath and is never lumped into the editable amount.
 */
const PartyAmountRow = ({ row, value, onChange, isNew, admin = 0, readOnly = false }) => {
    const avatarClass = isNew
        ? 'bg-teal-50 text-teal-700'
        : row.side === 'plaintiff'
          ? 'bg-teal-50 text-teal-700'
          : 'bg-amber-50 text-amber-800'
    return (
        <div className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-b-0">
            <div
                className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${avatarClass}`}
            >
                {initialsOf(row.label)}
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm text-gray-800 truncate">
                    {row.label}
                    {isNew && (
                        <span className="ml-1.5 align-middle rounded bg-teal-50 px-1.5 py-0.5 text-[10px] font-medium text-teal-700">
                            New
                        </span>
                    )}
                </div>
                <div className="text-[11px] text-gray-400">{row.roleLabel}</div>
            </div>
            <div className="flex items-center justify-end gap-2 flex-shrink-0">
                {readOnly ? (
                    <span className="w-28 text-right text-sm font-medium text-gray-800">${fmtMoney(value)}</span>
                ) : (
                    <input
                        type="text"
                        inputMode="decimal"
                        // Shown as currency ("$3,333.34") with the $ and the
                        // thousands comma, and no ".00" for whole dollars.
                        // onChange strips "$" and commas before parsing; the
                        // underlying value stays exact to the cent.
                        value={'$' + fmtMoney(value)}
                        onChange={(e) => onChange(round2(e.target.value.replace(/[^0-9.]/g, '')))}
                        className="w-28 text-right rounded-lg border border-gray-200 px-2 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-teal-600 focus:border-teal-600"
                    />
                )}
                {admin > 0 && (
                    <span className="text-xs font-medium text-teal-700 whitespace-nowrap">
                        + ${fmtFee(admin)} admin fee
                    </span>
                )}
            </div>
        </div>
    )
}

/** Chips email input — Enter, comma or blur commits the address. */
const EmailChipsInput = ({ emails, onChange, invalid }) => {
    const [text, setText] = useState('')

    const commit = (raw) => {
        const parts = String(raw)
            .split(/[,;\s]+/)
            .map((p) => p.trim())
            .filter(Boolean)
        if (!parts.length) return
        onChange([...emails, ...parts])
        setText('')
    }

    return (
        <div
            className={`flex flex-wrap items-center gap-1.5 rounded-lg border px-2 py-1.5 min-h-[42px] focus-within:ring-1 focus-within:ring-teal-600 focus-within:border-teal-600 ${
                invalid ? 'border-red-500' : 'border-gray-200'
            }`}
        >
            {emails.map((email, i) => (
                <span
                    key={`${email}-${i}`}
                    className="inline-flex items-center gap-1 rounded bg-gray-100 px-2 py-0.5 text-sm text-gray-800"
                >
                    {email}
                    <button
                        type="button"
                        aria-label={`Remove ${email}`}
                        onClick={() => onChange(emails.filter((_, idx) => idx !== i))}
                        className="rounded-[2px] px-0.5 leading-none text-gray-500 hover:bg-red-200 hover:text-red-700"
                    >
                        ×
                    </button>
                </span>
            ))}
            <input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                        e.preventDefault()
                        commit(text)
                    } else if (e.key === 'Backspace' && !text && emails.length) {
                        onChange(emails.slice(0, -1))
                    }
                }}
                // Commit on blur too — an address typed but not Entered would
                // otherwise be silently dropped on submit.
                onBlur={() => commit(text)}
                className="flex-1 min-w-[140px] border-0 px-1 py-0.5 text-sm focus:outline-none focus:ring-0"
            />
        </div>
    )
}

/** Email error → structured callout (duplicate-on-case lists the addresses). */
const EmailFieldError = ({ message }) => {
    const parsed = parseEmailFieldError(message)
    if (!parsed) return null
    if (parsed.kind === 'case_conflict') {
        const { emails } = parsed
        return (
            <div className="mt-2 rounded-lg border border-red-100 bg-red-50 p-3 shadow-sm">
                <div className="flex items-start gap-2">
                    <HiOutlineExclamationCircle size={15} className="text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-medium text-red-900">
                            {emails.length === 1
                                ? 'This address is already on the case'
                                : 'These addresses are already on the case'}
                        </p>
                        <p className="text-xs font-normal leading-snug text-red-800/90 mt-0.5">
                            Linked to an existing participant — remove or replace before saving.
                        </p>
                        <div className="mt-2 flex flex-wrap gap-1.5" role="list">
                            {emails.map((addr) => (
                                <span
                                    key={addr}
                                    role="listitem"
                                    className="inline-flex max-w-full items-center rounded border border-red-200/90 bg-white/70 px-2 py-0.5 font-mono text-[11px] leading-tight text-red-900 break-all ring-1 ring-inset ring-red-300/70"
                                >
                                    {addr}
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        )
    }
    return (
        <div className="mt-2 rounded-lg border border-red-100 bg-red-50 p-3 shadow-sm">
            <div className="flex items-start gap-2">
                <HiOutlineExclamationCircle size={15} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                    <p className="text-sm font-medium text-red-900">Check your email addresses</p>
                    <p className="text-xs font-normal leading-snug text-red-800/90 mt-0.5">{parsed.message}</p>
                </div>
            </div>
        </div>
    )
}

/**
 * A failed submit, shown in the dialog directly above the button that caused it.
 * Errors that come back from the backend are database-level sentences, so where
 * we can recognise one, `hint` explains what it means in the case's own terms.
 */
const SubmitError = ({ message, hint }) => {
    if (!message) return null
    return (
        <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3">
            <div className="flex items-start gap-2">
                <HiOutlineExclamationCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                    <p className="text-sm font-medium text-red-900">{message}</p>
                    {hint && <p className="text-xs text-red-800/90 leading-relaxed mt-1">{hint}</p>}
                </div>
            </div>
        </div>
    )
}

const BTN_PLAIN =
    'px-3 py-1.5 rounded-lg text-sm font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-50'
const BTN_SOLID =
    'px-4 py-1.5 rounded-lg bg-teal-800 text-white text-sm font-medium hover:bg-teal-700 disabled:opacity-40'
const BTN_DANGER =
    'px-4 py-1.5 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 disabled:opacity-40'

export default function ModifyPartiesModal({
    isOpen,
    onClose,
    caseId,
    side: sideProp = 'plaintiff',
    mode: modeProp = 'add',
    // A collectRemovableParties `key` to pre-select when opening in remove mode
    // (e.g. from a party row's trash icon). Additive; defaults to nothing.
    preselectRemoveKey = null,
    // "Invoices exist in the finances table" — the SAME boolean the case-detail
    // page computes, threaded in so the split step keys off exactly what the
    // Finances table shows rather than a re-derived approximation.
    hasInvoiceRecords = false,
    onToast,
    onRefresh,
}) {
    const {
        caseData,
        is_odr_mediator,
        isSystemCoupon,
        plaintiffPartyLabel,
        defendantPartyLabel,
    } = useContext(CaseDetailContext)

    // Hourly cases carry a $0 placeholder payment, so per-party amounts live on
    // the hourly invoices instead. cm-be already attaches those rows per party.
    const { amtMap: hourlyAmtByClient, adminMap: hourlyAdminByClient } = useMemo(
        () => buildHourlyMaps(collectHourlyRows(caseData)),
        [caseData]
    )

    // Whether an invoice already EXISTS on the finances table. This is the SOLE
    // distinction (NOT hourly vs fixed): the middle "Invoice split" step is
    // about EXISTING invoices, so no invoices -> nothing to split -> skip it.
    const showInvoiceStep = hasInvoiceRecords
    // Stricter gate for the "email the updated invoice" option: there must be an
    // ACTUALLY GENERATED invoice to send. A case can have finance rows with no
    // generated invoice yet, in which case the email option is hidden.
    const invoiceGenerated = hasGeneratedInvoice(caseData)

    const totalSteps = showInvoiceStep ? 3 : 2
    const stepLabels = showInvoiceStep
        ? ['Party type', 'Invoice split', 'Notifications']
        : ['Party type', 'Notifications']
    const removeSteps = showInvoiceStep
        ? ['Select party', 'Invoice split', 'Notifications']
        : ['Select party', 'Notifications']
    const removeTotalSteps = showInvoiceStep ? 3 : 2

    // NOTE: the paid-invoice gate is NOT here. Once any party has paid, the
    // Add Party / Remove Party buttons in CaseParticipants are disabled with a
    // tooltip saying why, so this modal is simply never opened on a locked case
    // and does not need a lock screen of its own. Both surfaces read the same
    // hasPaidInvoiceOnCase predicate, so they cannot disagree.

    const [mode, setMode] = useState(modeProp) // 'add' | 'remove'
    const [step, setStep] = useState(1)
    const [partyType, setPartyType] = useState(sideProp || 'plaintiff')
    const [name, setName] = useState('')
    const [emails, setEmails] = useState([])
    const [address, setAddress] = useState('')
    const [amounts, setAmounts] = useState({}) // { rowKey: number } — custom edits
    const [splitMode, setSplitMode] = useState('even') // 'even' | '5050' | 'custom'
    // Two independent notification toggles:
    //   notifyInvoiceParties      — re-issue invoices to onboarded parties
    //   sendNewPartyOnboardingLink — email the NEW party its onboarding link
    const [notifyInvoiceParties, setNotifyInvoiceParties] = useState(true)
    const [sendNewPartyOnboardingLink, setSendNewPartyOnboardingLink] = useState(true)
    const [formErrors, setFormErrors] = useState({})
    const [isSubmitting, setIsSubmitting] = useState(false)
    // A failed add/remove is shown INSIDE the dialog, next to the button that
    // caused it, rather than as a corner toast the user has to catch. The modal
    // stays open so the failure can be understood and acted on in place.
    const [submitError, setSubmitError] = useState('')

    // Remove-mode state
    const [selectedRemoveKey, setSelectedRemoveKey] = useState(null)
    const [isRemoving, setIsRemoving] = useState(false)
    // Overrides for the survivor amounts in the remove preview, keyed by row.
    const [removeAmounts, setRemoveAmounts] = useState({})
    // Replace mode: the removable-party `key` being replaced by the new party.
    const [replaceKey, setReplaceKey] = useState(null)

    useEffect(() => {
        if (isOpen) {
            setMode(modeProp)
            // Shared between add and remove — reset on open so the remove
            // Notifications step never inherits a stale toggled-off value.
            setNotifyInvoiceParties(true)
            if (modeProp === 'remove' && preselectRemoveKey) {
                const match = collectRemovableParties(caseData, is_odr_mediator, isSystemCoupon).find(
                    (p) => p.key === preselectRemoveKey
                )
                setSelectedRemoveKey(match ? match.key : null)
            }
        } else {
            setMode(modeProp)
            setStep(1)
            setPartyType(sideProp || 'plaintiff')
            setName('')
            setEmails([])
            setAddress('')
            setAmounts({})
            setSplitMode('even')
            setNotifyInvoiceParties(true)
            setSendNewPartyOnboardingLink(true)
            setFormErrors({})
            setSubmitError('')
            setSelectedRemoveKey(null)
            setRemoveAmounts({})
            setReplaceKey(null)
        }
    }, [isOpen, sideProp, modeProp, preselectRemoveKey, caseData, is_odr_mediator, isSystemCoupon])

    // Selecting a different party invalidates the previous survivor edits.
    useEffect(() => {
        setRemoveAmounts({})
        setSubmitError('')
    }, [selectedRemoveKey])

    const role = roleWord({ side: partyType, is_odr_mediator, isSystemCoupon })
    const emailsFiltered = (emails || [])
        .map((e) => (typeof e === 'string' ? e.trim() : e))
        .filter((e) => e !== '' && e != null)

    const removableParties = useMemo(
        () => collectRemovableParties(caseData, is_odr_mediator, isSystemCoupon),
        [caseData, is_odr_mediator, isSystemCoupon]
    )
    const selectedRemove = removableParties.find((p) => p.key === selectedRemoveKey) || null

    // The party being REPLACED (replace mode only).
    const replaceTarget = useMemo(
        () => removableParties.find((p) => p.key === replaceKey) || null,
        [removableParties, replaceKey]
    )
    const isReplaceMode = mode === 'add' && !!replaceTarget

    // ---- Add step 2: invoice rows -----------------------------------------
    // ALL current parties — the mediation POOL + admin lookup. In replace mode
    // the replaced party's amount STAYS in the pool: the new party inherits its
    // slot, so the mediation total is unchanged. It is only hidden from the list.
    const existingAll = useMemo(
        () => collectExistingParties(caseData, hourlyAmtByClient, hourlyAdminByClient),
        [caseData, hourlyAmtByClient, hourlyAdminByClient]
    )
    const existing = useMemo(() => {
        if (!replaceTarget) return existingAll
        return existingAll.filter((r) => {
            if (replaceTarget.isPrimary) return r.key !== replaceTarget.side
            const prefix = replaceTarget.side === 'plaintiff' ? 'p-add-' : 'd-add-'
            return r.key !== `${prefix}${replaceTarget.casePartyId}`
        })
    }, [existingAll, replaceTarget])

    // Mediation (base) POOL — every current party's BASE fee, including the one
    // being replaced. Even / 50-50 divide THIS pool; admin is added on top.
    const total = useMemo(() => round2(existingAll.reduce((s, r) => s + r.amount, 0)), [existingAll])

    // Flat admin fee per party, taken from a same-side party (fallback: any).
    // $0 when the mediator absorbs it. Always a whole dollar.
    const adminPerParty = useMemo(() => {
        const sameSide = existingAll.find((r) => r.side === partyType)
        return Math.round((sameSide ?? existingAll[0])?.admin ?? 0)
    }, [existingAll, partyType])

    const splitRows = useMemo(() => {
        const newRow = {
            key: '__new__',
            side: partyType,
            client_id: null,
            label: name.trim() || `New ${role}`,
            roleLabel: `New ${partyType === 'plaintiff' ? 'plaintiff' : 'defendant'}`,
            isNew: true,
            amount: 0,
            admin: adminPerParty,
        }
        return [...existing, newRow]
    }, [existing, name, role, partyType, adminPerParty])

    // Even split — the mediation pool shared equally as SUM-PRESERVING shares,
    // ordered by client_id (new party last) so the remainder lands on the same
    // rows the backend picks. Preview == bill.
    const computeEven = useMemo(() => {
        const n = splitRows.length
        if (!n) return {}
        const sorted = [...splitRows].sort(byClientIdAsc)
        const shares = splitWhole(total, n)
        const out = {}
        sorted.forEach((r, i) => {
            out[r.key] = shares[i]
        })
        return out
    }, [splitRows, total])

    // 50-50 — each SIDE shares 50% of the pool equally among its parties. The
    // pool is halved sum-preservingly first, then each half is split within the
    // side, so every side's base is exact too.
    const compute5050 = useMemo(() => {
        const plaintiffs = splitRows.filter((r) => r.side === 'plaintiff').sort(byClientIdAsc)
        const defendants = splitRows.filter((r) => r.side === 'defendant').sort(byClientIdAsc)
        const [plaintiffSideTotal, defendantSideTotal] = splitWhole(total, 2)
        const out = {}
        const apply = (list, sideTotal) => {
            const n = list.length
            if (!n) return
            const shares = splitWhole(sideTotal, n)
            list.forEach((r, i) => {
                out[r.key] = shares[i]
            })
        }
        apply(plaintiffs, plaintiffSideTotal)
        apply(defendants, defendantSideTotal)
        return out
    }, [splitRows, total])

    const valueForRow = (rowKey) => {
        const computed = splitMode === '5050' ? compute5050 : computeEven
        return amounts[rowKey] != null ? amounts[rowKey] : computed[rowKey] ?? 0
    }

    const valuedRows = splitRows.map((r) => {
        const base = valueForRow(r.key)
        const admin = Math.round(r.admin ?? 0) // flat admin — always whole dollars
        return { ...r, value: base, admin, grand: round2(base + admin) }
    })
    const editedSum = round2(valuedRows.reduce((s, r) => s + Number(r.value || 0), 0))
    const adminSum = round2(valuedRows.reduce((s, r) => s + Number(r.admin || 0), 0))
    const grandSum = round2(editedSum + adminSum)

    const setSplit = (next) => {
        setSplitMode(next)
        // Switching to even / 50-50 clears manual edits so computed values show.
        if (next !== 'custom') setAmounts({})
    }

    const onAmountEdit = (rowKey, val) => {
        // Any manual edit switches to Custom. On the FIRST edit, FREEZE every row
        // at its current displayed value so untouched parties don't move —
        // without this, custom mode falls back to computeEven for un-overridden
        // rows and the other parties jump.
        setAmounts((prev) => {
            if (splitMode !== 'custom') {
                const source = splitMode === '5050' ? compute5050 : computeEven
                const frozen = {}
                splitRows.forEach((r) => {
                    frozen[r.key] = prev[r.key] != null ? prev[r.key] : round2(source[r.key] ?? 0)
                })
                frozen[rowKey] = val
                return frozen
            }
            return { ...prev, [rowKey]: val }
        })
        setSplitMode('custom')
    }

    const validateStep1 = () => {
        const errs = validateAddParty({ name, emails: emailsFiltered })
        setFormErrors(errs)
        return Object.keys(errs).length === 0
    }

    const goNext = () => {
        if (step === 1 && !validateStep1()) return
        // No sum-to-total gate: the case manager may intentionally raise or lower
        // the amounts. Step 2 only surfaces an expected-vs-current note.
        setStep((s) => Math.min(s + 1, totalSteps))
    }
    const goBack = () => setStep((s) => Math.max(s - 1, 1))
    const removeGoNext = () => setStep((s) => Math.min(s + 1, removeTotalSteps))
    const removeGoBack = () => setStep((s) => Math.max(s - 1, 1))

    // ---- Remove mode -------------------------------------------------------

    const sideLabels = useMemo(
        () => ({ plaintiff: plaintiffPartyLabel, defendant: defendantPartyLabel }),
        [plaintiffPartyLabel, defendantPartyLabel]
    )

    // A PRIMARY party can only be removed if there is another party of the same
    // side to promote into the primary slot. That case offers a way forward
    // (replace), so it is rendered differently from the other blocks.
    const hasPromotableForSelected = useMemo(() => {
        if (!selectedRemove?.isPrimary) return true
        const sideData = selectedRemove.side === 'defendant' ? caseData?.defendant : caseData?.plaintiff
        return Array.isArray(sideData?.additionalParties) ? sideData.additionalParties.length > 0 : false
    }, [selectedRemove, caseData])

    // Every reason this party cannot be removed, checked here so it is shown on
    // the SELECT step rather than arriving as a raw database error after the
    // user has walked through the whole wizard. Invoices do NOT block removal:
    // like Add, removing a party voids the unpaid invoices and reissues them.
    const removeDisabledReason = useMemo(
        () => removalBlockedReason(selectedRemove, caseData, sideLabels),
        [selectedRemove, caseData, sideLabels]
    )

    const canRemove = !!selectedRemove && !removeDisabledReason

    /**
     * How the SURVIVING parties re-split once the selected party is removed.
     * The mediation total is PRESERVED and REDISTRIBUTED — the removed party's
     * share is shared out, so each remaining party pays more (3 @ $666 -> remove
     * one -> 2 @ $1,000). Under 50/50, only the removed party's SIDE re-splits.
     */
    const removePreview = useMemo(() => {
        if (!selectedRemove) return null
        const all = collectExistingParties(caseData, hourlyAmtByClient, hourlyAdminByClient)
        const removedKey = selectedRemove.isPrimary
            ? selectedRemove.side
            : `${selectedRemove.side === 'plaintiff' ? 'p-add-' : 'd-add-'}${selectedRemove.casePartyId}`
        const survivors = all.filter((r) => r.key !== removedKey)
        if (!survivors.length) return null

        const alloc = caseData?.invoice_allocation_party || 'equally_split'
        const bases = {}
        // Sum-preserving, ordered by client_id so the remainder matches the
        // backend (3 @ $167 -> remove one -> 2 @ $250, not $251).
        const splitEqual = (list, poolAmt) => {
            const n = list.length
            if (!n) return
            const sorted = [...list].sort(byClientIdAsc)
            const shares = splitWhole(poolAmt, n)
            sorted.forEach((r, i) => {
                bases[r.key] = shares[i]
            })
        }

        if (alloc === '50_percent' || alloc === '5050') {
            const side = selectedRemove.side
            const sideSurvivors = survivors.filter((r) => r.side === side)
            // Pool = that side's FULL share (incl. the removed party), redivided.
            const sidePool = round2(all.filter((r) => r.side === side).reduce((s, r) => s + r.amount, 0))
            splitEqual(sideSurvivors, sidePool)
            survivors
                .filter((r) => r.side !== side)
                .forEach((r) => {
                    bases[r.key] = round2(r.amount)
                })
        } else {
            splitEqual(survivors, round2(all.reduce((s, r) => s + r.amount, 0)))
        }

        const rows = survivors.map((r) => {
            const base = round2(bases[r.key] ?? r.amount)
            const admin = Math.round(r.admin ?? 0)
            return { ...r, value: base, admin, grand: base <= 0 ? 0 : round2(base + admin) }
        })
        return {
            rows,
            mediationTotal: round2(rows.reduce((s, r) => s + r.value, 0)),
            adminTotal: round2(rows.reduce((s, r) => s + r.admin, 0)),
            grandTotal: round2(rows.reduce((s, r) => s + r.grand, 0)),
        }
    }, [selectedRemove, caseData, hourlyAmtByClient, hourlyAdminByClient])

    const removeValuedRows = (removePreview?.rows || []).map((r) => {
        const value = removeAmounts[r.key] != null ? removeAmounts[r.key] : r.value
        return { ...r, value, grand: value <= 0 ? 0 : round2(value + r.admin) }
    })
    const removeEditedSum = round2(removeValuedRows.reduce((s, r) => s + Number(r.value || 0), 0))
    const removeAdminSum = round2(removeValuedRows.reduce((s, r) => s + Number(r.admin || 0), 0))
    const removeGrandSum = round2(removeEditedSum + removeAdminSum)

    /**
     * Replace the ONLY party of a side: jump to the Add wizard in replace mode.
     * The new party is added, then the old one removed; step 2 excludes the
     * replaced party from the split.
     */
    const startReplaceFlow = () => {
        if (!selectedRemove) return
        setReplaceKey(selectedRemoveKey)
        setPartyType(selectedRemove.side)
        setName('')
        setEmails([])
        setAddress('')
        setAmounts({})
        setSplitMode('even')
        setNotifyInvoiceParties(true)
        setSendNewPartyOnboardingLink(true)
        setFormErrors({})
        setStep(1)
        setMode('add')
    }

    // ---- Submits -----------------------------------------------------------

    const removeRequest = (target, body) =>
        target.isPrimary
            ? api.delete(`/cases/${caseId}/parties/primary/${target.side}`, { data: body })
            : api.delete(`/cases/${caseId}/parties/${target.casePartyId}`, { data: body })

    const handleSubmit = async () => {
        if (!validateStep1()) {
            setStep(1)
            return
        }
        if (!caseId) {
            setFormErrors({ email: 'Case is not loaded. Refresh the page and try again.' })
            return
        }
        setIsSubmitting(true)
        setFormErrors({})
        setSubmitError('')
        try {
            await api.post(`/cases/${caseId}/parties`, {
                party_type: partyType,
                name: name.trim(),
                emails: emailsFiltered,
                address: address.trim() || null,
                // Whenever the split step is shown we send the EXACT per-party
                // amounts the preview displays. We do NOT fall back to null for
                // an untouched even split: the backend RPC's own even split uses
                // a different remainder distribution, which made the saved
                // amounts differ from the preview by a cent.
                amounts: !showInvoiceStep
                    ? null
                    : valuedRows.map((r) => ({
                          client_id: r.client_id || null,
                          is_new: r.key === '__new__',
                          amount: Number(r.value),
                      })),
                notify_invoice_parties: invoiceGenerated ? notifyInvoiceParties : false,
                send_new_party_onboarding: sendNewPartyOnboardingLink,
            })

            // REPLACE: only AFTER the add resolved. For a primary target,
            // remove-primary promotes the just-added party (the side now has two,
            // so the guard is satisfied) and deletes the old primary.
            if (replaceTarget) {
                try {
                    await removeRequest(replaceTarget, { replace: true })
                } catch (removeErr) {
                    onRefresh?.()
                    onToast?.({
                        type: 'error',
                        message: 'New party added, but the original could not be removed — remove it manually.',
                    })
                    onClose?.()
                    return
                }
            }

            onRefresh?.()
            onToast?.({
                type: 'success',
                message: replaceTarget
                    ? `${replaceTarget.roleLabel} replaced.`
                    : partyType === 'plaintiff'
                      ? 'Additional plaintiff added.'
                      : 'Additional defendant added.',
            })
            onClose?.()
        } catch (saveErr) {
            const status = saveErr?.response?.status
            const msg =
                saveErr?.response?.data?.message || saveErr?.message || 'Could not add party. Please try again.'
            if (status === 400 && typeof msg === 'string' && /already used on this case/i.test(msg)) {
                setFormErrors({ email: msg })
                setStep(1)
                return
            }
            setSubmitError(msg)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleRemove = async () => {
        if (!selectedRemove || !canRemove || isRemoving || !caseId) return
        setIsRemoving(true)
        setSubmitError('')
        // Survivor amounts to persist. The removed party is already filtered out
        // of removePreview, so these are survivors only, keyed by client_id.
        const survivorAmounts = removeValuedRows.length
            ? removeValuedRows.map((r) => ({ client_id: r.client_id || null, amount: Number(r.value) }))
            : undefined
        try {
            await removeRequest(selectedRemove, {
                ...(survivorAmounts ? { amounts: survivorAmounts } : {}),
                notify_invoice_parties: invoiceGenerated ? notifyInvoiceParties : false,
            })
            onRefresh?.()
            onToast?.({
                type: 'success',
                message: selectedRemove.isPrimary
                    ? `${selectedRemove.side === 'plaintiff' ? 'Plaintiff' : 'Defendant'} removed and the next party promoted.`
                    : selectedRemove.side === 'plaintiff'
                      ? 'Additional plaintiff removed.'
                      : 'Additional defendant removed.',
            })
            onClose?.()
        } catch (err) {
            setSubmitError(
                err?.response?.data?.message || err?.message || 'Could not remove this party.'
            )
        } finally {
            setIsRemoving(false)
        }
    }

    /**
     * Translate a backend sentence into what it means on THIS case. The RPC
     * messages name a rule but not the row that broke it, which is what the
     * user actually needs in order to fix it.
     */
    const submitErrorHint = useMemo(() => {
        if (!submitError) return ''
        const m = String(submitError).toLowerCase()
        if (m.includes('cannot remove primary party')) {
            // The RPC compares the removed party's client_id against BOTH
            // primaries, so an additional party that reuses a primary's client
            // row trips this even though it is not itself a primary.
            return (
                removalBlockedReason(selectedRemove, caseData, sideLabels) ||
                "A case must always keep its primary plaintiff and defendant. Remove an additional party instead, or replace the primary."
            )
        }
        if (m.includes('a paid invoice already exists') || m.includes('cannot remove party: an invoice')) {
            return 'An invoice on this case has already been paid, so the parties can no longer be changed.'
        }
        if (m.includes('cannot remove the only')) {
            return 'This is the last party on its side. Add another party first, or use Replace.'
        }
        if (m.includes('case party not found')) {
            return 'This party is no longer on the case — someone may have removed it already. Close and reopen the case to refresh.'
        }
        return ''
    }, [submitError, selectedRemove, caseData, sideLabels])

    const isLastStep = step === totalSteps
    // "No email" = neither the invoice re-issue nor the onboarding link fires.
    const noEmailSelected = !sendNewPartyOnboardingLink && !(invoiceGenerated && notifyInvoiceParties)

    // ----- Header -----------------------------------------------------------
    const header = (
        <div className="px-6 py-3">
            <div className="flex items-center justify-between gap-3">
                <h2 className="text-lg font-semibold text-gray-900 tracking-tight leading-snug">
                    {isReplaceMode ? 'Replace party' : 'Modify parties'}
                </h2>
                <div className="flex items-center gap-2">
                    {!isReplaceMode && (
                        <div className="flex gap-1 rounded-lg bg-gray-100 p-1">
                            <button
                                type="button"
                                onClick={() => {
                                    setMode('add')
                                    setStep(1)
                                }}
                                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${mode === 'add' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                            >
                                Add party
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    setMode('remove')
                                    setStep(1)
                                }}
                                className={`rounded-md px-3 py-1 text-xs font-medium transition-colors ${mode === 'remove' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                            >
                                Remove party
                            </button>
                        </div>
                    )}
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        className="flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors"
                    >
                        <HiX size={18} />
                    </button>
                </div>
            </div>
            {mode === 'add' && (
                <p className="text-sm text-gray-600 mt-1">
                    {step === 1
                        ? 'Party details'
                        : step === 2 && showInvoiceStep
                          ? 'Confirm invoice split'
                          : 'Notifications'}
                    {` — step ${step} of ${totalSteps}`}
                </p>
            )}
            {mode === 'remove' && (
                <p className="text-sm text-gray-600 mt-1">
                    {step === 1
                        ? 'Select a party to remove from this case.'
                        : step === removeTotalSteps
                          ? 'Choose what to email.'
                          : 'Review how the remaining parties will be billed.'}
                    {` — step ${step} of ${removeTotalSteps}`}
                </p>
            )}
        </div>
    )

    // ----- Footer -----------------------------------------------------------
    let footer = null
    if (mode === 'add') {
            footer = (
                <div className="px-6 py-4 flex justify-between gap-2 border-t border-gray-100">
                    <button type="button" className={BTN_PLAIN} onClick={step === 1 ? onClose : goBack}>
                        {step === 1 ? 'Cancel' : 'Back'}
                    </button>
                    {isLastStep ? (
                        <button type="button" className={BTN_SOLID} disabled={isSubmitting} onClick={handleSubmit}>
                            {isSubmitting
                                ? 'Saving…'
                                : partyType === 'plaintiff'
                                  ? 'Add Plaintiff'
                                  : 'Add Defendant'}
                        </button>
                    ) : (
                        <button type="button" className={BTN_SOLID} onClick={goNext}>
                            Next
                        </button>
                    )}
                </div>
            )
        } else {
            // Remove mirrors Add: step 1 picks a party, the split step previews
            // the re-divide, the final step confirms. Only step 1 → Next carries
            // the canRemove guard.
            const onFinalStep = step === removeTotalSteps
            const blocked = step === 1 && !canRemove
            footer = (
                <div className="px-6 py-4 flex justify-between gap-2 border-t border-gray-100">
                    <button type="button" className={BTN_PLAIN} onClick={step === 1 ? onClose : removeGoBack}>
                        {step === 1 ? 'Cancel' : 'Back'}
                    </button>
                    {/* The wrapper carries the not-allowed cursor: a disabled
                        <button> ignores pointer events, so the cursor and the
                        tooltip have to live on the wrapper. */}
                    <span
                        className={blocked || (onFinalStep && (!canRemove || isRemoving)) ? 'cursor-not-allowed' : ''}
                        title={!canRemove && removeDisabledReason ? removeDisabledReason : undefined}
                    >
                        {!onFinalStep ? (
                            <button
                                type="button"
                                className={`${BTN_SOLID} ${blocked ? 'pointer-events-none' : ''}`}
                                disabled={blocked}
                                onClick={removeGoNext}
                            >
                                Next
                            </button>
                        ) : (
                            <button
                                type="button"
                                className={`${BTN_DANGER} ${!canRemove || isRemoving ? 'pointer-events-none' : ''}`}
                                disabled={!canRemove || isRemoving}
                                onClick={handleRemove}
                            >
                                {isRemoving ? 'Removing…' : 'Remove party'}
                            </button>
                        )}
                    </span>
                </div>
            )
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            width="max-w-[700px]"
            header={header}
            footer={footer}
            bodyClassName="p-0"
            overlayClassName="bg-gray-900/50 backdrop-blur-[5px]"
        >
            {<StepBar step={step} steps={mode === 'add' ? stepLabels : removeSteps} />}

            <div className="px-6 py-5">
                {/* ===================== ADD — STEP 1 ===================== */}
                {mode === 'add' && step === 1 && (
                    <div className="flex flex-col gap-y-4">
                        <div>
                            <label className="text-black pb-1 block text-sm font-medium">
                                Which side is this party on?
                            </label>
                            <div className="flex gap-2">
                                {['plaintiff', 'defendant'].map((t) => (
                                    <button
                                        key={t}
                                        type="button"
                                        onClick={() => setPartyType(t)}
                                        className={`flex-1 rounded-lg border px-3 py-2 text-sm font-medium capitalize ${partyType === t ? 'border-teal-800 bg-teal-50 text-teal-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                                    >
                                        {roleWord({ side: t, is_odr_mediator, isSystemCoupon })}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="text-black pb-1 block text-sm font-medium">Counsel name</label>
                            <input
                                type="text"
                                value={name}
                                onChange={(e) => {
                                    setName(e.target.value)
                                    if (formErrors.name) setFormErrors((p) => ({ ...p, name: undefined }))
                                }}
                                placeholder={`Enter ${role} counsel name`}
                                className={`w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-600 focus:border-teal-600 ${formErrors.name ? 'border-red-500' : 'border-gray-200'}`}
                            />
                            {formErrors.name && <div className="text-red-500 text-sm mt-1">{formErrors.name}</div>}
                        </div>
                        <div>
                            <label className="text-black pb-1 block text-sm font-medium">Counsel email</label>
                            <EmailChipsInput
                                emails={emails}
                                invalid={!!formErrors.email}
                                onChange={(updated) => {
                                    setEmails(updated)
                                    if (formErrors.email) setFormErrors((p) => ({ ...p, email: undefined }))
                                }}
                            />
                            {formErrors.email ? <EmailFieldError message={formErrors.email} /> : null}
                            <span className="font-semibold text-xs py-1 flex items-center italic text-gray-600">
                                Add multiple emails by pressing Enter
                            </span>
                        </div>
                        <div>
                            <label className="text-black pb-1 block text-sm font-medium">
                                Address <span className="text-gray-400 font-normal">(optional)</span>
                            </label>
                            <input
                                type="text"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                                placeholder="Mailing address for the invoice (optional)"
                                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-teal-600 focus:border-teal-600"
                            />
                        </div>
                    </div>
                )}

                {/* ===================== ADD — STEP 2 (invoice split) ===================== */}
                {mode === 'add' && step === 2 && showInvoiceStep && (
                    <div className="flex flex-col gap-y-3">
                        {isReplaceMode && (
                            <div className="flex items-start gap-2 rounded-lg border border-teal-100 bg-teal-50 px-3 py-2">
                                <HiOutlineExclamationCircle size={15} className="text-teal-600 flex-shrink-0 mt-0.5" />
                                <span className="text-xs text-gray-700 leading-relaxed">
                                    Replacing{' '}
                                    <span className="font-medium text-gray-900">
                                        {replaceTarget.roleLabel}: {replaceTarget.label}
                                    </span>
                                    . The new party takes their place; the old party is removed once the new one is
                                    added.
                                </span>
                            </div>
                        )}
                        {invoiceGenerated && (
                            <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                                <HiOutlineExclamationCircle size={15} className="text-gray-400 flex-shrink-0 mt-0.5" />
                                <span className="text-xs text-gray-600 leading-relaxed">
                                    Invoices already sent. Updated amounts will be emailed to affected parties.
                                </span>
                            </div>
                        )}

                        <div>
                            <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-2">
                                Split method
                            </p>
                            <div className="flex gap-1.5 rounded-lg bg-gray-100 p-1">
                                <button
                                    type="button"
                                    onClick={() => setSplit('even')}
                                    className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${splitMode === 'even' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                                >
                                    Even split
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setSplit('5050')}
                                    className={`flex-1 rounded-md px-2 py-1.5 text-xs font-medium transition-colors ${splitMode === '5050' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500'}`}
                                >
                                    50/50 split
                                </button>
                                {splitMode === 'custom' && (
                                    <button
                                        type="button"
                                        className="flex-1 rounded-md bg-white px-2 py-1.5 text-xs font-medium text-gray-900 shadow-sm"
                                    >
                                        Custom
                                    </button>
                                )}
                            </div>
                            <p className="text-[11px] text-gray-400 mt-2">
                                {splitMode === '5050'
                                    ? 'Plaintiffs share 50% equally. Defendants share 50% equally.'
                                    : splitMode === 'even'
                                      ? 'All parties share the total equally regardless of side.'
                                      : 'Custom amounts — total may differ from original.'}
                            </p>
                        </div>

                        {valuedRows.some((r) => r.side === 'plaintiff') && (
                            <div>
                                <div className="border-b border-gray-100 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                    Plaintiffs
                                </div>
                                {valuedRows
                                    .filter((r) => r.side === 'plaintiff')
                                    .map((r) => (
                                        <PartyAmountRow
                                            key={r.key}
                                            row={r}
                                            value={r.value}
                                            admin={r.admin}
                                            isNew={r.isNew}
                                            onChange={(v) => onAmountEdit(r.key, v)}
                                        />
                                    ))}
                            </div>
                        )}

                        {valuedRows.some((r) => r.side === 'defendant') && (
                            <div>
                                <div className="border-b border-gray-100 pb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                    Defendants
                                </div>
                                {valuedRows
                                    .filter((r) => r.side === 'defendant')
                                    .map((r) => (
                                        <PartyAmountRow
                                            key={r.key}
                                            row={r}
                                            value={r.value}
                                            admin={r.admin}
                                            isNew={r.isNew}
                                            onChange={(v) => onAmountEdit(r.key, v)}
                                        />
                                    ))}
                            </div>
                        )}

                        <div className="border-t border-gray-100 pt-2.5 space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500">Subtotal</span>
                                <span className="text-sm text-gray-700">${fmtMoney(editedSum)}</span>
                            </div>
                            {adminSum > 0 && (
                                <div className="flex justify-between items-center">
                                    <span className="flex items-center gap-1.5 text-sm font-medium text-teal-700">
                                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-teal-100 text-[11px] leading-none text-teal-700">
                                            +
                                        </span>
                                        Admin fee
                                    </span>
                                    <span className="text-sm font-medium text-teal-700">${fmtMoney(adminSum)}</span>
                                </div>
                            )}
                            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                <span className="text-base font-bold text-gray-900">New total</span>
                                <span className="text-base font-bold text-gray-900">${fmtMoney(grandSum)}</span>
                            </div>
                        </div>

                        {splitMode === 'custom' && Math.abs(editedSum - total) >= 0.01 && (
                            <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                                <HiOutlineExclamationCircle size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />
                                <span className="text-xs text-gray-600 leading-relaxed">
                                    Expected total ${fmtMoney(total)} — current total ${fmtMoney(editedSum)}
                                    {editedSum < total
                                        ? ` ($${fmtMoney(round2(total - editedSum))} less than expected).`
                                        : ` ($${fmtMoney(round2(editedSum - total))} more than expected).`}{' '}
                                    You can still proceed.
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* ===================== ADD — final step: notifications ===================== */}
                {mode === 'add' && step === totalSteps && (
                    <div className="flex flex-col gap-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                            Notifications
                        </p>
                        {/* Invoice re-issue — only relevant once invoices exist. */}
                        {invoiceGenerated && (
                            <button
                                type="button"
                                onClick={() => setNotifyInvoiceParties((v) => !v)}
                                className={`flex items-start gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors ${notifyInvoiceParties ? 'border-teal-800 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}
                            >
                                <HiOutlineMail
                                    size={18}
                                    className={`mt-0.5 flex-shrink-0 ${notifyInvoiceParties ? 'text-teal-800' : 'text-gray-400'}`}
                                />
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900 mb-0.5">
                                        Email all parties their updated invoice
                                    </div>
                                    <div className="text-[11px] text-gray-500 leading-relaxed">
                                        The invoice amounts changed. Send each party who already has an invoice their
                                        new amount. (Parties who haven&apos;t onboarded yet don&apos;t have an
                                        invoice, so they won&apos;t get an email.)
                                    </div>
                                </div>
                                <span
                                    className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border ${notifyInvoiceParties ? 'border-teal-800 bg-teal-800 text-white' : 'border-gray-300'}`}
                                >
                                    {notifyInvoiceParties && <HiCheck size={12} />}
                                </span>
                            </button>
                        )}
                        {/* New-party onboarding link — independent toggle. */}
                        <button
                            type="button"
                            onClick={() => setSendNewPartyOnboardingLink((v) => !v)}
                            className={`flex items-start gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors ${sendNewPartyOnboardingLink ? 'border-teal-800 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}
                        >
                            <HiOutlineMail
                                size={18}
                                className={`mt-0.5 flex-shrink-0 ${sendNewPartyOnboardingLink ? 'text-teal-800' : 'text-gray-400'}`}
                            />
                            <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 mb-0.5">
                                    {`Email ${name.trim() || `the new ${role}`} a link to set up their account`}
                                </div>
                                <div className="text-[11px] text-gray-500 leading-relaxed">
                                    {`${name.trim() || `The new ${role}`} will get an email with the case details and a link to complete onboarding.`}
                                </div>
                            </div>
                            <span
                                className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border ${sendNewPartyOnboardingLink ? 'border-teal-800 bg-teal-800 text-white' : 'border-gray-300'}`}
                            >
                                {sendNewPartyOnboardingLink && <HiCheck size={12} />}
                            </span>
                        </button>
                        {/* Explicit "no email" — turns BOTH toggles off. */}
                        <button
                            type="button"
                            onClick={() => {
                                setNotifyInvoiceParties(false)
                                setSendNewPartyOnboardingLink(false)
                            }}
                            className={`flex items-start gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors ${noEmailSelected ? 'border-teal-800 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}
                        >
                            <HiOutlineBan
                                size={18}
                                className={`mt-0.5 flex-shrink-0 ${noEmailSelected ? 'text-teal-800' : 'text-gray-400'}`}
                            />
                            <div className="flex-1">
                                <div className="text-sm font-medium text-gray-900 mb-0.5">
                                    Don&apos;t send an email right now
                                </div>
                                <div className="text-[11px] text-gray-500 leading-relaxed">
                                    {`${name.trim() || `The new ${role}`} is added to the case, but no email goes out. You can send them an invoice or onboarding link later from the case page.`}
                                </div>
                            </div>
                            <span
                                className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border ${noEmailSelected ? 'border-teal-800 bg-teal-800 text-white' : 'border-gray-300'}`}
                            >
                                {noEmailSelected && <HiCheck size={12} />}
                            </span>
                        </button>
                        <SubmitError message={submitError} hint={submitErrorHint} />
                    </div>
                )}

                {/* ===================== REMOVE — STEP 1: select party ===================== */}
                {mode === 'remove' && step === 1 && (
                    <div className="flex flex-col gap-y-1">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                            Select party to remove
                        </p>
                        {removableParties.length === 0 ? (
                            <p className="text-sm text-gray-500 py-4">No removable parties on this case.</p>
                        ) : (
                            removableParties.map((p) => {
                                const selected = p.key === selectedRemoveKey
                                const avatarClass =
                                    p.side === 'plaintiff' ? 'bg-teal-50 text-teal-700' : 'bg-amber-50 text-amber-800'
                                return (
                                    <button
                                        key={p.key}
                                        type="button"
                                        onClick={() => setSelectedRemoveKey(p.key)}
                                        className={`flex items-center gap-3 rounded-lg px-2 py-2.5 text-left transition-colors ${selected ? 'bg-gray-100' : 'hover:bg-gray-50'}`}
                                    >
                                        <div
                                            className={`flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full text-[11px] font-semibold ${avatarClass}`}
                                        >
                                            {initialsOf(p.label)}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="text-sm text-gray-800 truncate">{p.label}</div>
                                            <div className="text-[11px] text-gray-400">
                                                {p.roleLabel}
                                                {p.isPrimary ? ' · Primary' : ''}
                                            </div>
                                        </div>
                                        {selected ? (
                                            <HiOutlineCheckCircle size={18} className="text-gray-600 flex-shrink-0" />
                                        ) : (
                                            <span className="h-[18px] w-[18px] flex-shrink-0 rounded-full border-2 border-gray-200" />
                                        )}
                                    </button>
                                )
                            })
                        )}
                        {selectedRemove && selectedRemove.isPrimary && !hasPromotableForSelected ? (
                            // Last of its side — blocked, but there IS a way
                            // forward, so offer it instead of a dead end.
                            <div className="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-3">
                                <div className="flex items-start gap-2">
                                    <HiOutlineExclamationCircle
                                        size={15}
                                        className="text-amber-500 flex-shrink-0 mt-0.5"
                                    />
                                    <div className="flex-1">
                                        <p className="text-xs text-gray-700 leading-relaxed">
                                            A case must always keep at least one {selectedRemove.side}. To remove this{' '}
                                            {selectedRemove.side}, replace them with a new party.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={startReplaceFlow}
                                            className="mt-2 inline-flex items-center rounded-md bg-teal-800 px-3 py-1.5 text-xs font-medium text-white"
                                        >
                                            Replace this {selectedRemove.side}
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ) : selectedRemove && removeDisabledReason ? (
                            // Any other block (e.g. the party shares a client
                            // record with a primary). Stated here rather than
                            // hidden in the disabled button's tooltip, and
                            // rather than arriving as a raw database error
                            // after the user walks the whole wizard.
                            <div className="mt-2 rounded-lg border border-red-200 bg-red-50 px-3 py-3">
                                <div className="flex items-start gap-2">
                                    <HiOutlineExclamationCircle
                                        size={15}
                                        className="text-red-600 flex-shrink-0 mt-0.5"
                                    />
                                    <div className="flex-1">
                                        <p className="text-sm font-medium text-red-900">
                                            This party can&apos;t be removed
                                        </p>
                                        <p className="text-xs text-red-800/90 leading-relaxed mt-1">
                                            {removeDisabledReason}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        ) : selectedRemove ? (
                            <div className="mt-2 flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                                <HiOutlineExclamationCircle size={15} className="text-gray-400 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-gray-600 leading-relaxed">
                                    Removing{' '}
                                    <span className="font-medium text-gray-900">{selectedRemove.label}</span> will
                                    permanently delete them from this case
                                    {selectedRemove.isPrimary
                                        ? ' and promote the next party of that side into the primary slot'
                                        : ''}
                                    . This can&apos;t be undone.
                                </p>
                            </div>
                        ) : null}
                    </div>
                )}

                {/* ===================== REMOVE — STEP 2: split preview ===================== */}
                {mode === 'remove' && showInvoiceStep && step === 2 && selectedRemove && removePreview && (
                    <div className="flex flex-col gap-y-3">
                        <div className="flex items-start gap-2 rounded-lg border border-teal-100 bg-teal-50/60 px-3 py-2.5">
                            <HiOutlineExclamationCircle size={15} className="text-teal-700 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-gray-700 leading-relaxed">
                                Removing <span className="font-medium text-gray-900">{selectedRemove.label}</span> (
                                {selectedRemove.roleLabel}). The remaining parties re-split the mediation fee as shown
                                below.
                                {(caseData?.invoice_allocation_party === '50_percent' ||
                                    caseData?.invoice_allocation_party === '5050') &&
                                    ` Only the ${selectedRemove.side} side is re-divided.`}
                            </p>
                        </div>
                        {['plaintiff', 'defendant'].map((side) => {
                            const sideRows = removeValuedRows.filter((r) => r.side === side)
                            if (!sideRows.length) return null
                            return (
                                <div key={side}>
                                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                                        {side === 'plaintiff' ? 'Plaintiffs' : 'Defendants'}
                                    </p>
                                    {sideRows.map((r) => (
                                        <PartyAmountRow
                                            key={r.key}
                                            row={r}
                                            value={r.value}
                                            admin={r.admin}
                                            onChange={(v) =>
                                                setRemoveAmounts((prev) => ({ ...prev, [r.key]: round2(v) }))
                                            }
                                        />
                                    ))}
                                </div>
                            )
                        })}
                        <div className="border-t border-gray-100 pt-2.5 space-y-2">
                            <div className="flex justify-between items-center">
                                <span className="text-sm text-gray-500">Subtotal</span>
                                <span className="text-sm text-gray-700">${fmtMoney(removeEditedSum)}</span>
                            </div>
                            {removeAdminSum > 0 && (
                                <div className="flex justify-between items-center">
                                    <span className="flex items-center gap-1.5 text-sm font-medium text-teal-700">
                                        <span className="flex h-4 w-4 items-center justify-center rounded-full bg-teal-100 text-[11px] leading-none text-teal-700">
                                            +
                                        </span>
                                        Admin fee
                                    </span>
                                    <span className="text-sm font-medium text-teal-700">
                                        ${fmtMoney(removeAdminSum)}
                                    </span>
                                </div>
                            )}
                            <div className="flex justify-between items-center pt-2 border-t border-gray-100">
                                <span className="text-base font-bold text-gray-900">New total</span>
                                <span className="text-base font-bold text-gray-900">${fmtMoney(removeGrandSum)}</span>
                            </div>
                        </div>

                        {Math.abs(removeEditedSum - removePreview.mediationTotal) >= 0.01 && (
                            <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2">
                                <HiOutlineExclamationCircle size={15} className="text-amber-500 flex-shrink-0 mt-0.5" />
                                <span className="text-xs text-gray-600 leading-relaxed">
                                    Expected total ${fmtFee(removePreview.mediationTotal)} — current total $
                                    {fmtFee(removeEditedSum)}
                                    {removeEditedSum < removePreview.mediationTotal
                                        ? ` ($${fmtFee(round2(removePreview.mediationTotal - removeEditedSum))} less than expected).`
                                        : ` ($${fmtFee(round2(removeEditedSum - removePreview.mediationTotal))} more than expected).`}{' '}
                                    You can still proceed.
                                </span>
                            </div>
                        )}
                    </div>
                )}

                {/* ===================== REMOVE — final step: notifications ===================== */}
                {mode === 'remove' && step === removeTotalSteps && (
                    <div className="flex flex-col gap-y-2">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400 mb-1">
                            Notifications
                        </p>
                        {invoiceGenerated ? (
                            <>
                                <button
                                    type="button"
                                    onClick={() => setNotifyInvoiceParties((v) => !v)}
                                    className={`flex items-start gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors ${notifyInvoiceParties ? 'border-teal-800 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}
                                >
                                    <HiOutlineMail
                                        size={18}
                                        className={`mt-0.5 flex-shrink-0 ${notifyInvoiceParties ? 'text-teal-800' : 'text-gray-400'}`}
                                    />
                                    <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900 mb-0.5">
                                            Email the updated invoice to all parties
                                        </div>
                                        <div className="text-[11px] text-gray-500 leading-relaxed">
                                            The invoice amounts changed — re-send every party that has a Stripe
                                            invoice their updated invoice. (Parties who haven&apos;t onboarded yet
                                            have no invoice, so they aren&apos;t emailed.)
                                        </div>
                                    </div>
                                    <span
                                        className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border ${notifyInvoiceParties ? 'border-teal-800 bg-teal-800 text-white' : 'border-gray-300'}`}
                                    >
                                        {notifyInvoiceParties && <HiCheck size={12} />}
                                    </span>
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setNotifyInvoiceParties(false)}
                                    className={`flex items-start gap-3 rounded-lg border px-3.5 py-3 text-left transition-colors ${!notifyInvoiceParties ? 'border-teal-800 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}
                                >
                                    <HiOutlineBan
                                        size={18}
                                        className={`mt-0.5 flex-shrink-0 ${!notifyInvoiceParties ? 'text-teal-800' : 'text-gray-400'}`}
                                    />
                                    <div className="flex-1">
                                        <div className="text-sm font-medium text-gray-900 mb-0.5">
                                            Don&apos;t send any email
                                        </div>
                                        <div className="text-[11px] text-gray-500 leading-relaxed">
                                            The party is removed silently — no updated invoice is emailed. You can
                                            email invoices later from the case.
                                        </div>
                                    </div>
                                    <span
                                        className={`mt-0.5 flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full border ${!notifyInvoiceParties ? 'border-teal-800 bg-teal-800 text-white' : 'border-gray-300'}`}
                                    >
                                        {!notifyInvoiceParties && <HiCheck size={12} />}
                                    </span>
                                </button>
                            </>
                        ) : (
                            <div className="flex items-start gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5">
                                <HiOutlineExclamationCircle size={15} className="text-gray-400 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-gray-600 leading-relaxed">
                                    No invoices exist yet — nothing to email.
                                </p>
                            </div>
                        )}
                        <SubmitError message={submitError} hint={submitErrorHint} />
                    </div>
                )}
            </div>
        </Modal>
    )
}
